import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, H3, Hero, In, JsonLd, List, P, Related, Src, Table, WhatIs } from "@/components/seo/kit";

// /alternatives/zapier. Plan: gtm/products/clearmakes-inbox/seo/pages/alternatives-zapier.md
// Honest alternatives page for email and chat automation. Every competitor fact was opened
// on 2026-09-29 and is linked on the page. Re-check prices before changing this page.

const PATH = "/alternatives/zapier";
const CHECKED = "29 September 2026";

type Option = { name: string; url: string; source: string; price: string; unit: string; suits: string; not: string };

const OPTIONS: Option[] = [
  {
    name: "Make",
    url: "https://www.make.com",
    source: "https://www.make.com/en/pricing",
    price: "Free: 1,000 credits a month, 2 active scenarios, 15-minute interval. Paid from $9 a month for 5,000 credits, 1-minute interval.",
    unit: "Credits (one operation is one credit)",
    suits: "High volume on a small budget, if you like building on a visual canvas.",
    not: "People who want to describe a job in words and not build it step by step.",
  },
  {
    name: "n8n",
    url: "https://n8n.io",
    source: "https://n8n.io/pricing",
    price: "Community Edition is free if you host it yourself. Cloud Starter €20 a month billed yearly for 2,500 executions.",
    unit: "Executions (a whole run, however many steps)",
    suits: "Teams with someone technical who wants control, or to self-host.",
    not: "Beginners: Capterra reviewers call the learning curve “very steep”.",
  },
  {
    name: "Microsoft Power Automate",
    url: "https://www.microsoft.com/en-us/power-platform/products/power-automate",
    source: "https://www.microsoft.com/en-us/power-platform/products/power-automate/pricing",
    price: "Free licence for work or school accounts with standard connectors (Outlook, Teams). Premium $15 a user a month, paid yearly.",
    unit: "Per user, or per bot",
    suits: "Companies on Microsoft 365 moving Outlook email into Teams.",
    not: "Gmail and Slack teams; premium connectors need a paid licence.",
  },
  {
    name: "Google Workspace Studio",
    url: "https://workspace.google.com/studio/",
    source: "https://support.google.com/workspace-studio/answer/16444479",
    price: "Included in Google Workspace Business Starter, Standard and Plus, and Enterprise Standard and Plus.",
    unit: "Your Workspace seat",
    suits: "Teams already paying for Google Workspace who automate inside Gmail and Google apps.",
    not: "Outlook or Teams users; personal Gmail accounts.",
  },
  {
    name: "IFTTT",
    url: "https://ifttt.com",
    source: "https://ifttt.com/plans",
    price: "Free: 2 Applets. Pro $2.99 a month; Pro+ $8.99 a month.",
    unit: "Number of Applets",
    suits: "One or two very simple personal rules.",
    not: "Business workflows with several steps; Outlook checks run hourly on Free and every 5 minutes on Pro.",
  },
  {
    name: "ClearMakes Inbox (us)",
    url: "https://clearmakes.com/products/inbox",
    source: "https://clearmakes.com/pricing",
    price: "Free: 3 apps, 2 new workflows and 100 runs a month. Starter $29 a month for 5,000 runs; Growth $59 a month, unlimited.",
    unit: "Runs (one workflow start, with all its steps)",
    suits: "Small teams whose work arrives as email and chat, who want to describe the job in one sentence.",
    not: "Anything that doesn't start from a message, such as data syncs between databases, spreadsheets or form tools. Zapier or Make fit those better.",
  },
];

const faqs = [
  {
    q: "What is the cheapest Zapier alternative?",
    a: "For simple rules, IFTTT Pro at $2.99 a month; for volume, Make from $9 a month for 5,000 credits; and n8n is free if you host it yourself. Cheapest depends on how you count: tasks, credits, executions and runs all measure different things.",
  },
  {
    q: "Is ClearMakes Inbox cheaper than Zapier?",
    a: "At the same price it counts more generously for multi-step work. Zapier Professional is $29.99 a month for 750 tasks, and each successful action is a task. Inbox Starter is $29 a month for 5,000 runs, and a run includes all of its steps. Zapier connects many more apps directly.",
  },
  {
    q: "Can these tools build a workflow from a plain-English sentence?",
    a: "Most can now. Zapier has Copilot, Make has Maia, n8n has an AI Workflow Builder, Power Automate has Copilot and Google Workspace Studio uses Gemini. ClearMakes Inbox asks at most one question and only offers tools from apps you have connected.",
  },
  {
    q: "Is Relay.app still an option?",
    a: "No. Relay.app's website says it shut down in September 2026 and that all accounts, workflows and run history were permanently deleted. Some “best Zapier alternatives” lists still include it.",
  },
  {
    q: "Should I leave Zapier at all?",
    a: "Not if your Zaps are working and the bill is fine. Switching costs time. Consider it when the bill grows faster than the work, or when most of your Zaps start from an email or a chat message.",
  },
];

const itemList = {
  "@type": "ItemList",
  name: "Zapier alternatives for email and chat automation",
  itemListElement: OPTIONS.map((o, i) => ({ "@type": "ListItem", position: i + 1, name: o.name.replace(" (us)", ""), url: o.url })),
};

const AlternativesZapier = () => (
  <>
    <PageMeta
      title="Zapier Alternative for Email Automation: 6 Honest Picks"
      description="Six Zapier alternatives for email and chat automation, with prices checked on 29 September 2026, who each one suits, and when to stay on Zapier."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Zapier alternatives", path: PATH },
      ]}
    />
    <JsonLd data={itemList} />
    <main>
      <Hero
        eyebrow="Alternatives · Zapier"
        title="Zapier alternatives for email and chat automation"
        lead="Six honest options for teams whose automations start from an email or a chat message, with prices we checked ourselves and who each one is not for. We make one of them."
        secondary={{ label: "Jump to the comparison", href: "#compare" }}
      />

      <WhatIs title="What is a Zapier alternative for email automation?">
        A Zapier alternative for email automation is another tool that runs steps for you when an email or chat message arrives: posting it to
        Slack, adding a contact to HubSpot, or drafting a reply. The main differences are how each one charges (tasks, credits, executions or
        runs), how you build workflows, and which apps it reaches.
      </WhatIs>

      <Band
        tone="pale"
        title="Why people look for a Zapier alternative"
        answer="Mostly the bill, and Zaps that stop without saying so. Zapier is still the tool with the most app connections; the complaints are about paying per action and watching for failures."
      >
        <List
          items={[
            <>“Burned through my task limit in two days.” (<Src href="https://www.trustpilot.com/review/zapier.com">Trustpilot</Src>)</>,
            <>“Pricing scales up fast once you're running two separate use cases.” (<Src href="https://www.capterra.com/p/130182/Zapier/reviews/">Capterra</Src>)</>,
            <>“You often only notice a broken Zap when downstream data goes missing.” (<Src href="https://www.capterra.com/p/130182/Zapier/reviews/?page=2">Capterra</Src>)</>,
            <>“Check the zaps all the time in case they have stopped working.” (<Src href="https://www.trustpilot.com/review/zapier.com?page=3">Trustpilot</Src>)</>,
          ]}
        />
        <P>
          Reviews read on {CHECKED}. To be fair to Zapier: triggers, filters, paths, formatter and delay steps don't use tasks; only successful
          actions do. Its free plan is 100 tasks a month with two-step Zaps checked every 15 minutes; Professional is $29.99 a month (or $19.99 a
          month billed yearly) for 750 tasks, checked every 2 minutes. Source: <Src href="https://zapier.com/pricing">Zapier pricing</Src>.
        </P>
      </Band>

      <Band
        tone="white"
        id="compare"
        title="Six alternatives, compared"
        answer="There is no single best choice. Make and n8n are the closest general replacements; Power Automate and Google Workspace Studio are often already paid for; ClearMakes Inbox is narrower and built around messages."
      >
        <Table
          caption="Zapier alternatives compared"
          head={["Tool", "Price (checked " + CHECKED + ")", "What it charges for", "Best for", "Not for"]}
          rows={OPTIONS.map((o) => [
            o.name,
            <>
              {o.price} <Src href={o.source}>Source</Src>
            </>,
            o.unit,
            o.suits,
            o.not,
          ])}
          note={
            <>
              Zapier for reference: free 100 tasks a month; Professional $29.99 a month for 750 tasks (<Src href="https://zapier.com/pricing">zapier.com/pricing</Src>). n8n prices are
              shown in euros on its site. Prices change: check each page before you buy.
            </>
          }
        />
      </Band>

      <Band
        tone="pale"
        title="Where ClearMakes Inbox is different"
        answer="Inbox only does work that starts from a message, and you describe it in one sentence. That makes it smaller than Zapier, and simpler for email and chat jobs."
      >
        <H3>What you get</H3>
        <List
          items={[
            "A one-sentence planner that asks at most one question, about behaviour (should a reply send itself or wait for you?), never about wording.",
            "Steps chosen only from apps you have connected, so a workflow can't be saved with a tool that doesn't exist.",
            "Waiting steps that survive our updates: delays, retries and “wait for a reply” are saved, so a workflow picks up where it left off.",
            "Smart FAQ: saved answers to the questions you get every day.",
            "Your own MCP server or HTTP endpoint as a step, next to Gmail, Outlook, Slack, Teams, Notion, HubSpot and thousands more apps in the catalogue.",
            "Every run listed with each step and what went out.",
          ]}
        />
        <H3>What you give up</H3>
        <List
          items={[
            "Zapier's size: far more ready-made templates, and years of community answers.",
            "Workflows that don't start from a message (form entries, spreadsheet rows, database syncs).",
            "A long track record. ClearMakes is a young company; there are no public reviews of Inbox yet.",
          ]}
        />
      </Band>

      <Band tone="white" title="How to move one Zap to Inbox" answer="Move one Zap at a time, starting with one that begins with a new email. Run both side by side for a few days, then switch the Zap off." narrow>
        <List
          items={[
            "Pick a Zap whose trigger is a new email in Gmail or Outlook, or a new Slack or Teams message.",
            "Write down what it does in one sentence, the way you'd explain it to a colleague.",
            <>Connect the same apps in <In href="/products/inbox">ClearMakes Inbox</In>, type the sentence and check the steps.</>,
            "Leave both on for a few days and compare the runs, then switch the Zap off.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/gmail-to-slack", label: "Gmail to Slack automation", desc: "The most common Zap, set up in one sentence." },
          { href: "/integrations/email-to-hubspot", label: "Add email leads to HubSpot", desc: "Contacts with the details filled in." },
          { href: "/integrations/outlook-to-teams", label: "Outlook emails to Teams", desc: "Including the free Microsoft ways." },
          { href: "/pricing", label: "ClearMakes Inbox pricing", desc: "Free, Starter, Growth and Enterprise." },
          { href: "/workflows", label: "Describe a workflow in plain English", desc: "How the one-sentence planner works." },
          { href: "/guides", label: "Guides", desc: "Step-by-step guides for email and chat work." },
        ]}
      />

      <Closing title="Try one email workflow before you switch anything." line="Connect your mailbox, type one sentence, and compare the runs." />
    </main>
    <Footer />
  </>
);

export default AlternativesZapier;
